import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../services/api';
import { STATUT_LIST, statutLabel } from './constants';

const DOW = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export default function MaintenancePlanning({ currentUser, onOpen }) {
  const [cursor, setCursor] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  // Grille de 6 semaines (42 jours), démarrage lundi
  const cells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7; // lundi = 0
    const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1 - offset);
    return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }, [cursor]);

  const load = useCallback(async () => {
    setLoading(true); setDenied(false);
    try {
      const { data } = await api.get('/maintenance/demandes', { params: { from: ymd(cells[0]), to: ymd(cells[41]) } });
      setRows(data);
    } catch (err) {
      if (err.response?.status === 403) setDenied(true);
      setRows([]);
    } finally { setLoading(false); }
  }, [cells]);
  useEffect(() => { load(); }, [load]);

  const byDay = useMemo(() => {
    const m = {};
    for (const d of rows) { if (!d.pivot_ymd) continue; (m[d.pivot_ymd] = m[d.pivot_ymd] || []).push(d); }
    return m;
  }, [rows]);

  const today = ymd(new Date());
  const month = cursor.getMonth();
  const monthLabel = cursor.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const shift = (n) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + n, 1));
  const goToday = () => { const n = new Date(); setCursor(new Date(n.getFullYear(), n.getMonth(), 1)); };

  if (denied) return <div className="table-container"><div className="table-empty">Vous n'avez pas accès au planning de maintenance.</div></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--section-gap)' }}>
      <div className="mnt-cal-head">
        <button className="btn-icon" onClick={() => shift(-1)} title="Mois précédent">‹</button>
        <button className="btn-icon" onClick={() => shift(1)} title="Mois suivant">›</button>
        <button className="btn-secondary" onClick={goToday}>Aujourd'hui</button>
        <span className="mnt-cal-title">{monthLabel}</span>
        {loading && <span className="mnt-muted">chargement…</span>}
      </div>

      <div className="mnt-cal-grid">
        {DOW.map((d, i) => <div key={`dow${i}`} className="mnt-cal-dow">{d}</div>)}
        {cells.map((day) => {
          const key = ymd(day);
          const items = byDay[key] || [];
          const outside = day.getMonth() !== month;
          return (
            <div key={key} className={`mnt-cal-cell${outside ? ' outside' : ''}${key === today ? ' today' : ''}`}>
              <span className="mnt-cal-daynum">{day.getDate()}</span>
              {items.slice(0, 3).map((d) => (
                <button
                  key={d.id}
                  className={`mnt-cal-item${d.is_overdue ? ' overdue' : ''}`}
                  title={`${d.numero} · ${d.titre} — ${statutLabel(d.statut)}${d.is_overdue ? ' (en retard)' : ''}`}
                  onClick={() => onOpen(d.id)}
                >
                  <span className={`mnt-cal-dot s-${d.statut}`} />
                  <span className="mnt-cal-item-txt">{d.titre}</span>
                </button>
              ))}
              {items.length > 3 && <span className="mnt-cal-more">+{items.length - 3} autre(s)</span>}
            </div>
          );
        })}
      </div>

      <div className="mnt-cal-legend">
        {STATUT_LIST.filter((s) => !['refusee', 'annulee'].includes(s)).map((s) => (
          <span key={s} className="mnt-cal-legend-item"><span className={`mnt-cal-dot s-${s}`} />{statutLabel(s)}</span>
        ))}
        <span className="mnt-cal-legend-item"><span className="mnt-cal-swatch-overdue" />En retard</span>
      </div>
    </div>
  );
}
